// One "possible duplicate" in the review queue: the two families side by side, why they look alike, and the decision.

import React from "react";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import MergeTypeIcon from "@mui/icons-material/MergeType";
import CloseIcon from "@mui/icons-material/Close";
import type { CrmCustomer, MergeSuggestion } from "../../shared/crm";
import { ServiceBadges, StageChip, money } from "./ui";

const CONFIDENCE_COLOR = { high: "success", medium: "warning", low: "default" } as const;

const Side: React.FC<{ customer: CrmCustomer }> = ({ customer }) => (
  <Box sx={{ flex: 1, minWidth: 0, p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
      <Typography fontWeight={600}>{customer.name}</Typography>
      <StageChip customer={customer} />
    </Stack>
    <Stack direction="row" spacing={0.5} sx={{ mb: 0.5 }} flexWrap="wrap" useFlexGap><ServiceBadges services={customer.services} /></Stack>
    <Typography variant="body2">{customer.children.map((c) => c.name).join(", ") || "No children"}</Typography>
    {customer.emails.map((e) => <Typography key={e} variant="body2" color="text.secondary" noWrap>{e}</Typography>)}
    {customer.phones.map((p) => <Typography key={p} variant="body2" color="text.secondary">{p}</Typography>)}
    {customer.addresses.slice(0, 1).map((x) => <Typography key={x} variant="body2" color="text.secondary" noWrap>{x}</Typography>)}
    <Typography variant="caption" color="text.secondary">
      {customer.identities.length} record{customer.identities.length === 1 ? "" : "s"} · {money(customer.realised.invoicedPence)} invoiced
    </Typography>
  </Box>
);

export const DuplicateCard: React.FC<{
  suggestion: MergeSuggestion;
  a: CrmCustomer;
  b: CrmCustomer;
  busy: boolean;
  onMerge: () => void;
  onReject: () => void;
}> = ({ suggestion, a, b, busy, onMerge, onReject }) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
      <Chip size="small" color={CONFIDENCE_COLOR[suggestion.confidence]}
        label={`${suggestion.confidence[0]!.toUpperCase()}${suggestion.confidence.slice(1)} · ${Math.round(suggestion.score * 100)}%`} />
      {suggestion.reasons.map((r) => <Chip key={r.label} size="small" variant="outlined" label={r.label} />)}
    </Stack>
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
      <Side customer={a} />
      <Side customer={b} />
    </Stack>
    <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
      <Button size="small" startIcon={<CloseIcon />} disabled={busy} onClick={onReject}>Not the same</Button>
      <Button size="small" variant="contained" startIcon={<MergeTypeIcon />} disabled={busy} onClick={onMerge}>Same family · merge</Button>
    </Stack>
  </Paper>
);
